import React, { useState } from 'react';
import {
    Box,
    Card,
    CardActionArea,
    Chip,
    Dialog,
    Divider,
    Grid,
    Slide,
    Stack,
    Typography,
    useMediaQuery,
    Button,
} from '@mui/material';
import { TransitionProps } from '@mui/material/transitions';
import { useTheme } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import TravelExploreIcon from '@mui/icons-material/TravelExplore';
import GroupsIcon from '@mui/icons-material/Groups';
import DirectionsBoatIcon from '@mui/icons-material/DirectionsBoat';
import CelebrationIcon from '@mui/icons-material/Celebration';
import { openVisitTypeSliceAction } from '../../../../app/Slices/VisitTypeSlice';

const Transition = React.forwardRef(function Transition(
    props: TransitionProps & { children: React.ReactElement },
    ref: React.Ref<unknown>,
) {
    return <Slide direction='up' ref={ref} {...props} />;
});

type VisitType = 'business' | 'personal';

type Props = {
    open: boolean;
    onClose: () => void;
    recommended?: VisitType;
    heroTitle?: string;
    heroSubtitle?: string;
    ctaBusiness?: string;
    ctaPersonal?: string;
    illustration?: React.ReactNode;
    onSelect?: (type: VisitType) => void;
    rememberByDefault?: boolean;
};

export default function VisitTypeModal({
    open,
    onClose,
    recommended,
    heroTitle = 'Choose how you want to explore',
    heroSubtitle = 'Tailored experiences for teams and individuals.',
    ctaBusiness = 'Browse business',
    ctaPersonal = 'Browse personal',
    illustration,
    onSelect,
    rememberByDefault = true,
}: Props) {
    const theme = useTheme();
    const c = theme.palette;
    const isXs = useMediaQuery(theme.breakpoints.down('sm'));
    const isMdDown = useMediaQuery(theme.breakpoints.down('md'));
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [remember, setRemember] = useState(rememberByDefault);

    const handleGo = (type: VisitType) => {
        if (remember) localStorage.setItem('visitTypePref', type);
        onSelect?.(type);
        navigate(`/viewExperiences/${type}`);
        dispatch(openVisitTypeSliceAction({ view: type }));

        onClose();
    };

    const Feature = ({
        icon,
        text,
    }: {
        icon: React.ReactNode;
        text: string;
    }) => (
        <Stack direction='row' alignItems='center' spacing={1.2}>
            <Box
                sx={{
                    width: { xs: 26, md: 28 },
                    height: { xs: 26, md: 28 },
                    borderRadius: '50%',
                    display: 'grid',
                    placeItems: 'center',
                    bgcolor: c.background.paper,
                    color: c.primary.main,
                    border: `1px solid ${c.divider}`,
                    fontSize: { xs: 18, md: 20 },
                    flexShrink: 0,
                }}
            >
                {icon}
            </Box>
            <Typography variant='caption' sx={{ fontSize: { xs: 14, md: 16 } }}>
                {text}
            </Typography>
        </Stack>
    );

    const CardShell: React.FC<{
        type: VisitType;
        title: string;
        features: string[];
        ctaLabel: string;
        chipColor?: 'default' | 'primary' | 'success';
    }> = ({ type, title, features, ctaLabel }) => {
        const isRec = recommended === type;
        const featureIcons: React.ReactNode[] =
            type === 'personal'
                ? [
                      <CalendarMonthIcon fontSize='inherit' />,
                      <GroupsIcon fontSize='inherit' />,
                      <CelebrationIcon fontSize='inherit' />,
                  ]
                : [
                      <CalendarMonthIcon fontSize='inherit' />,
                      <GroupsIcon fontSize='inherit' />,
                      <DirectionsBoatIcon fontSize='inherit' />,
                  ];

        return (
            <Card
                elevation={0}
                sx={{
                    position: 'relative',
                    borderRadius: 3,
                    border: `1px solid ${c.primary.main}`,
                    background: c.background.paper,
                    transition: 'transform .16s ease, box-shadow .16s ease',
                    '@media (hover:hover)': {
                        '&:hover': {
                            transform: 'translateY(-2px)',
                            boxShadow: '0 16px 36px rgba(20,55,74,.12)',
                        },
                    },
                }}
            >
                {isRec && (
                    <Chip
                        label='Recommended'
                        color='default'
                        sx={{
                            position: 'absolute',
                            top: 12,
                            right: 12,
                            bgcolor: c.secondary.main,
                            color: c.primary.contrastText,
                            fontWeight: 700,
                            height: 26,
                        }}
                    />
                )}

                <CardActionArea
                    onClick={() => handleGo(type)}
                    sx={{
                        p: { xs: 1.75, md: 2.25 },
                        '&:focus-visible': {
                            outline: `3px solid ${c.secondary.main}`,
                            outlineOffset: 3,
                            borderRadius: 12,
                        },
                    }}
                >
                    <Stack spacing={1.25}>
                        <Typography
                            variant='h6'
                            sx={{
                                fontSize: { xs: 18, md: 20 },
                                color: c.primary.main,
                                fontWeight: 700,
                            }}
                        >
                            {title}
                        </Typography>

                        <Stack spacing={1} mt={0.5}>
                            {features.map((f, i) => (
                                <Feature
                                    key={i}
                                    icon={
                                        featureIcons[i] ?? (
                                            <CelebrationIcon fontSize='inherit' />
                                        )
                                    }
                                    text={f}
                                />
                            ))}
                        </Stack>

                        <Button
                            variant='contained'
                            fullWidth={isMdDown}
                            onClick={(e) => {
                                e.stopPropagation();
                                handleGo(type);
                            }}
                            sx={{
                                mt: 1,
                                alignSelf: { xs: 'stretch', md: 'flex-start' },
                                textTransform: 'none',
                                bgcolor: c.primary.main,
                                '&:hover': { bgcolor: c.primary.dark },
                                py: { xs: 1, md: 0.75 },
                            }}
                        >
                            {ctaLabel}
                        </Button>
                    </Stack>
                </CardActionArea>
            </Card>
        );
    };

    return (
        <Dialog
            open={open}
            onClose={(event, reason) => {
                if (reason !== 'backdropClick' && reason !== 'escapeKeyDown') {
                    onClose();
                }
            }}
            TransitionComponent={Transition}
            fullScreen={isXs}
            maxWidth='lg'
            PaperProps={{
                sx: {
                    overflow: 'hidden',
                    borderRadius: { xs: 0, sm: 3, md: 4 },
                    boxShadow: {
                        xs: 'none',
                        sm: '0 30px 80px rgba(20,55,74,.35)',
                    },
                    width: { xs: '100vw', sm: '92vw', md: 980 },
                    bgcolor: c.background.default,
                },
            }}
            BackdropProps={{ sx: { backgroundColor: 'rgba(0,0,0,0.7)' } }}
        >
            <Grid container>
                <Grid
                    item
                    xs={12}
                    md={5}
                    sx={{
                        position: 'relative',
                        minHeight: { xs: 180, md: 480 },
                        background: `linear-gradient(135deg, ${c.primary.main} 0%, ${c.primary.dark} 40%, ${c.secondary.main} 150%)`,
                        color: c.neutral.main,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        p: { xs: 2.25, md: 4 },
                    }}
                >
                    <Box
                        aria-hidden
                        sx={{
                            position: 'absolute',
                            inset: 0,
                            opacity: 0.08,
                            backgroundImage: `radial-gradient(${c.neutral.main} 1.2px, transparent 1.2px), radial-gradient(${c.neutral.main} 1.2px, transparent 1.2px)`,
                            backgroundSize: '22px 22px, 30px 30px',
                            backgroundPosition: '0 0, 11px 11px',
                            animation: 'float 9s ease-in-out infinite',
                            '@keyframes float': {
                                '0%,100%': { transform: 'translateY(0)' },
                                '50%': { transform: 'translateY(-6px)' },
                            },
                        }}
                    />
                    <Stack
                        spacing={1.5}
                        alignItems='center'
                        sx={{ position: 'relative' }}
                    >
                        {illustration ? (
                            <Box sx={{ mb: 0.5 }}>{illustration}</Box>
                        ) : (
                            <Box
                                sx={{
                                    width: { xs: 56, md: 64 },
                                    height: { xs: 56, md: 64 },
                                    borderRadius: '16px',
                                    display: 'grid',
                                    placeItems: 'center',
                                    bgcolor: 'rgba(255,255,255,.18)',
                                    border: '1px solid rgba(255,255,255,.35)',
                                    boxShadow: '0 10px 30px rgba(0,0,0,.15)',
                                    backdropFilter: 'blur(4px)',
                                }}
                            >
                                <TravelExploreIcon
                                    sx={{
                                        fontSize: { xs: 32, md: 36 },
                                        color: c.neutral.main,
                                    }}
                                />
                            </Box>
                        )}

                        <Typography
                            sx={{
                                color: c.neutral.main,
                                textAlign: 'center',
                                fontSize: { xs: 22, md: 36 },
                                lineHeight: { xs: '30px', md: '44px' },
                                fontWeight: 800,
                                textShadow: '0 2px 12px rgba(0,0,0,.25)',
                            }}
                        >
                            {heroTitle}
                        </Typography>

                        <Typography
                            sx={{
                                color: c.neutral.main,
                                textAlign: 'center',
                                fontSize: { xs: 14, md: 16 },
                                fontWeight: 500,
                                opacity: 0.95,
                            }}
                        >
                            {heroSubtitle}
                        </Typography>
                    </Stack>
                </Grid>

                <Grid
                    item
                    xs={12}
                    md={7}
                    sx={{
                        p: { xs: 2, md: 4 },
                        backgroundColor: c.background.default,
                    }}
                >
                    <Stack spacing={2}>
                        <Typography
                            sx={{
                                fontSize: { xs: 16, md: 18 },
                                fontWeight: 700,
                                color: c.primary.main,
                                textAlign: 'center',
                            }}
                        >
                            Choose your path
                        </Typography>

                        <Stack
                            direction={{ xs: 'column', md: 'row' }}
                            spacing={2}
                            justifyContent='center'
                        >
                            <CardShell
                                type='business'
                                title='For Businesses'
                                features={[
                                    'Retreats and offices',
                                    'Team experiences',
                                    'Work perks that wow',
                                ]}
                                ctaLabel={ctaBusiness}
                            />
                            <CardShell
                                type='personal'
                                title='For Personal'
                                features={[
                                    'Cars and stays',
                                    'Unique experiences',
                                    'Weekends made easy',
                                ]}
                                ctaLabel={ctaPersonal}
                            />
                        </Stack>
                        <Divider
                            sx={{
                                display: { xs: 'none', md: 'block' },
                                my: { xs: 1, md: 1.5 },
                                borderBottomWidth: 3,
                                borderColor: c.secondary.main,
                                color: c.secondary.main,
                            }}
                        />

                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'center',
                                minHeight: 56,
                            }}
                        >
                            <Button
                                variant={isMdDown ? 'contained' : 'outlined'}
                                color={isMdDown ? 'secondary' : 'primary'}
                                onClick={() => navigate('/reservations')}
                                fullWidth={isMdDown}
                                sx={{
                                    textTransform: 'none',
                                    borderRadius: 2,
                                    px: { xs: 2, md: 3 },
                                    py: { xs: 1, md: 0.75 },
                                    '&:hover': {
                                        bgcolor: isMdDown
                                            ? c.primary.main
                                            : c.secondary.light,
                                        color: isMdDown
                                            ? c.primary.contrastText
                                            : c.primary.main,
                                    },
                                }}
                            >
                                Manage reservations
                            </Button>
                        </Box>
                    </Stack>
                </Grid>
            </Grid>
        </Dialog>
    );
}
